import styled, { css } from 'styled-components';
import color from 'color';
import React from 'react';
import theme from '../../lib/theme';

const Btn = styled.button.withConfig({
  shouldForwardProp: (props) => {
    return !['outline', 'block', 'round', 'disable', 'htmlType', 'link'].includes(props);
  },
})`
  display: inline-block;
  font-style: normal;
  font-weight: normal;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  vertical-align: middle;
  cursor: pointer;
  user-select: none;
  border: 1px solid transparent;

  padding: 7px 1em;
  font-size: var(--fs-md);
  border-radius: 4px;

  transition: all 0.2s ease-in-out;
  text-decoration: none;

  &.disabled,
  &.disabled:hover,
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
  }

  &.lgx {
    padding: 16px 1.5em;
    font-size: var(--fs-md);
    border-radius: 6px;
  }
  &.lg {
    padding: 11px 1.5em;
    font-size: var(--fs-md);
    border-radius: 6px;
  }

  &.sm {
    padding: 5px 1em;
    font-size: var(--fs-base);
  }

  &.xs {
    padding: 2px 0.5em;
    font-size: 10px;
  }

  &.round {
    border-radius: 100px;
  }

  &.block {
    display: block;
    width: 100%;
  }

  &.link {
    font-weight: normal;
    border-radius: 0;

    &:hover,
    &:active,
    &.active {
      background-color: transparent;
      border-color: transparent;
      box-shadow: none;
    }
  }
  &:hover {
    opacity: 0.9;
  }
`;

const BtnNoBorder = styled(Btn)`
  padding: 8px 1em;
  &.lgx {
    padding: 17px 1.5em;
  }
  &.lg {
    padding: 12px 1.5em;
  }

  &.sm {
    padding: 6px 1em;
  }

  &.xs {
    padding: 3px 0.2em;
  }
`;

const getSolid = ($color: string) => {
  const $dark = color($color).darken(0.1).hex();

  return css`
    color: #fff;
    background-color: ${$color};
    border-color: ${$color};

    &:hover {
      color: #fff;
      background-color: ${$dark};
      border-color: ${$dark};
    }
  `;
};
const getOutline = ($color: string, $fontColor: string = $color) => {
  return css`
    background-image: none;
    background-color: transparent;
    color: ${$fontColor} !important;
    border-color: ${$color};

    &:hover {
      color: #fff !important;
      background-color: ${$color};
      border-color: ${$color};
    }
  `;
};
export const BtnPrimary = styled(BtnNoBorder)`
  ${getSolid(theme.color.primary)}
  border-color: transparent!important;
  background: linear-gradient(90deg, #c669ff 0%, #7555f7 100%) no-repeat;
  border: none;
`;

export const BtnWarnLinear = styled(BtnNoBorder)`
  ${getSolid(theme.color.warn)}
  border-color: transparent!important;
  background: ${theme.colorGradient.warn} no-repeat;
  border: none;
`;

export const BtnPrimaryOutline = styled(Btn)`
  ${getOutline(theme.color.primary)}
`;
export const BtnSecond = styled(Btn)`
  ${getSolid(theme.color.second)}
`;
export const BtnSecondOutline = styled(Btn)`
  ${getOutline(theme.color.second)}
`;
export const BtnDefault = styled(Btn)`
  ${getSolid(theme.color.secondary)}
`;
export const BtnDefaultOutline = styled(Btn)`
  ${getOutline('#999', '#C3C3C3')}
`;
export const BtnInfo = styled(Btn)`
  ${getSolid(theme.color.info)}
`;
export const BtnInfoOutline = styled(Btn)`
  ${getOutline(theme.color.info)}
`;
export const BtnWarn = styled(Btn)`
  ${getSolid(theme.color.warn)}
`;
export const BtnWarnOutline = styled(Btn)`
  ${getOutline(theme.color.warn)}
`;
export const BtnSuccess = styled(Btn)`
  ${getSolid(theme.color.success)}
`;
export const BtnSuccessOutline = styled(Btn)`
  ${getOutline(theme.color.success)}
`;

export const BtnDark = styled(Btn)`
  ${getSolid(theme.color.dark)}
`;
export const BtnDarkOutline = styled(Btn)`
  ${getOutline(theme.color.dark)}
`;

export const BtnWhite = styled(Btn)`
  ${getSolid(theme.color.white)}
  color: ${theme.color.dark};

  &:hover {
    color: ${theme.color.dark};
  }
`;
export const BtnWhiteOutline = styled(Btn)`
  ${getOutline(theme.color.white)}
  &:hover {
    color: ${theme.color.primary} !important;
    border-color: ${theme.color.primary} !important;
    background-color: transparent;
  }
`;

const buttons = {
  primary: BtnPrimary,
  second: BtnSecond,
  default: BtnDefault,
  info: BtnInfo,
  white: BtnWhite,
  dark: BtnDark,
  warn: BtnWarn,
  warnLinear: BtnWarnLinear,
  success: BtnSuccess,
};

const outlineButtons = {
  primary: BtnPrimaryOutline,
  second: BtnSecondOutline,
  default: BtnDefaultOutline,
  info: BtnInfoOutline,
  white: BtnWhiteOutline,
  dark: BtnDarkOutline,
  warn: BtnWarnOutline,
  success: BtnSuccessOutline,
};

type IProps = {
  size?: 'sm' | 'lg' | 'xs' | 'lgx' | '';
  type?:
    | 'primary'
    | 'second'
    | 'default'
    | 'info'
    | 'white'
    | 'dark'
    | 'warn'
    | 'warnLinear'
    | 'success';
  block?: boolean;
  round?: boolean;
  outline?: boolean;
  disabled?: boolean;
  disable?: boolean;
  link?: boolean;
  className?: string;
  htmlType?: 'submit' | 'reset' | 'button';
  children?: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLElement>;
};

export const Button = (props: IProps): JSX.Element => {
  const {
    size = '',
    type = 'primary',
    round = false,
    block = false,
    outline = false,
    disabled = false,
    disable = false,
    link = false,
    className = '',
    htmlType = 'button',
    onClick = (e) => {},
    ...rest
  } = props;

  let BtnCom = buttons[type];
  if (outline) {
    BtnCom = outlineButtons[type];
  }

  const handleClick = (e) => {
    if (disable || disabled) {
      return;
    }
    onClick && onClick(e);
  };

  return (
    <BtnCom
      className={`_btn ${className} ${size} ${round ? 'round ' : ''}${block ? 'block ' : ''}${
        disabled || disable ? 'disabled ' : ''
      }`}
      type={htmlType}
      onClick={handleClick}
      {...rest}
    />
  );
};
